import { Box, Tooltip } from '@material-ui/core';
import {
  DocumentationRequiredType,
  DocumentStatus,
  formatDateTimeReadable,
  PaymentMethodType,
  RequiredFile,
} from '@nemuru-eng/js-api-client';
import { useParams } from 'react-router-dom';

import { SetDocumentStatusForm } from '@/components/modules/Documentation/components/DocumentationDetails/components/SetDocumentStatusForm';
import { UploadFileManager } from '@/components/modules/Documentation/components/DocumentationDetails/components/UploadFileManager/UploadFileManager';
import { useRequiredFile } from '@/components/modules/Documentation/components/DocumentationDetails/hooks/useRequiredFile';
import { DocumentDetailsAction } from '@/components/modules/Documentation/components/DocumentationDetails/types/documentDetailsActions';
import { DocumentDetailsStandardAction } from '@/components/modules/Documentation/components/DocumentationHeader/types/DocumentDetailsStandardAction';
import { hashLinkId } from '@/components/modules/Documentation/constants/hashLinkId';
import { useDocStatuses } from '@/components/modules/Documentation/hooks/useDocStatuses';
import { DocumentationMode } from '@/components/modules/Documentation/types/DocumentationMode';
import { DocumentStatusWithSent } from '@/components/modules/Documentation/types/DocumentStatusWithSent';
import { ConditionalWrapper } from '@/components/shared/components/ConditionalWrapper';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { Font } from '@/components/shared/components/Font';
import { MoreActionsMenuWithIcon } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenuWithIcon';
import { StandardActionDialog } from '@/components/shared/components/StandardActionDialog/StandardActionDialog';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

interface RequiredFileCardProps {
  requiredFile: RequiredFile;
  documentationRequiredType: DocumentationRequiredType;
  paymentMethodType: PaymentMethodType;
  documentationRequiredId: string;
  mode: DocumentationMode;
  paymentMethodId?: string;
  borrowerId?: string;
}

export const RequiredFileCard = ({
  requiredFile,
  paymentMethodType,
  documentationRequiredType,
  documentationRequiredId,
  mode,
  paymentMethodId,
  borrowerId,
}: RequiredFileCardProps) => {
  const { customBorderRadius } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();
  const docStatuses = useDocStatuses();
  const { xs } = useScreen();
  const { loanId } = useParams();

  const id = paymentMethodId || loanId;

  if (!id) return null;

  const {
    actionOptionsArray,
    actionDialogParams,
    actionStandardDialogParams,
    uploadFile,
    setActionDialogParams,
    resetUseRequiredFileState,
    opened,
    setOpened,
    loading,
  } = useRequiredFile({
    requiredFile,
    paymentMethodType,
    documentationRequiredType,
    documentationRequiredId,
    paymentMethodId: id,
    borrowerId,
    mode,
  });

  const shouldDisplayAnnotation = requiredFile?.annotation && !requiredFile.isSent;

  const getStatusPropsByRequiredFilesItem = (requiredFile: RequiredFile) => {
    if (requiredFile.isSent && requiredFile.status !== DocumentStatus.INCIDENCE) {
      return docStatuses[DocumentStatusWithSent.SENT];
    }
    return docStatuses[requiredFile.status];
  };

  const actionsAllowed = actionOptionsArray.length > 0;

  return (
    <>
      <CustomPaper
        fill={'paper'}
        style={{
          borderRadius: customBorderRadius.paper,
          padding: '6px 16px 6px 4px',
        }}
      >
        <Box height={'40px'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <Box
            bgcolor={getStatusPropsByRequiredFilesItem(requiredFile)?.color}
            style={{
              minWidth: '24px',
              width: ' 24px',
              height: '24px',
              borderRadius: customBorderRadius.paper,
              textAlign: 'center',
              color: getStatusPropsByRequiredFilesItem(requiredFile)?.textColor,
              marginBottom: '-1px',
              marginLeft: '8px',
              marginRight: '16px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Font variant={'caption'}>
              <strong>{requiredFile.uploadedFiles?.length}</strong>
            </Font>
          </Box>
          <Box flex={1} display={'flex'} flexDirection={'column'} width={!xs && '220px'}>
            <Tooltip enterDelay={500} placement={'top'} title={<>{getTextIdTranslation(requiredFile.name)}</>}>
              <Box className={'overflow-text-ellipsis'}>
                <Font variant={'body2'} color={'title'} id={hashLinkId}>
                  <strong>
                    <Text tId={requiredFile.name} />
                  </strong>
                </Font>
              </Box>
            </Tooltip>
            <ConditionalWrapper
              condition={!!shouldDisplayAnnotation}
              wrapper={(children) => (
                <Tooltip enterDelay={300} title={requiredFile.annotation ?? ''}>
                  {children}
                </Tooltip>
              )}
            >
              <Box className={'overflow-text-ellipsis'}>
                <Font variant={'caption'} color={'textSecondary'}>
                  {shouldDisplayAnnotation ? (
                    <>
                      <Text tId={requiredFile.status} />
                      {requiredFile.annotation && `: ${requiredFile.annotation}`}
                    </>
                  ) : (
                    <Text
                      tId={getStatusPropsByRequiredFilesItem(requiredFile).descriptionTId}
                      dynamicDataTexts={requiredFile.sentDate ? [formatDateTimeReadable(requiredFile.sentDate)] : []}
                    />
                  )}
                </Font>
              </Box>
            </ConditionalWrapper>
          </Box>
          {actionsAllowed && <MoreActionsMenuWithIcon actionsOptionsArray={actionOptionsArray} />}
        </Box>
      </CustomPaper>
      {(actionStandardDialogParams?.actionType === DocumentDetailsStandardAction.SET_DOCUMENT_REQUIRED_IS_NOT_SENT ||
        actionStandardDialogParams?.actionType ===
          DocumentDetailsStandardAction.REMOVE_REQUIRED_FILE_TYPE_FROM_DOCUMENTATION_REQUIRED) &&
        opened && (
          <StandardActionDialog
            opened={opened}
            setOpened={setOpened}
            setActionDialogParams={setActionDialogParams}
            titleTextElement={actionStandardDialogParams.titleTextElement}
            actionDescriptionTextElement={actionStandardDialogParams.actionDescriptionTextElement}
            actionButtonsArray={actionStandardDialogParams.actionButtonsArray}
            loading={loading}
          />
        )}
      {actionDialogParams?.actionType === DocumentDetailsAction.SET_DOCUMENT_STATUS && opened && (
        <EditDialogWrapper
          opened={opened}
          setOpened={setOpened}
          handleClose={() => setOpened(false)}
          saveButtonTextId={actionDialogParams.saveButtonTextId}
          title={actionDialogParams.titleTextElement}
          onSubmitForm={actionDialogParams.onSubmitForm}
          isLoading={loading}
          defaultValues={{ documentStatus: requiredFile.status, annotation: null }}
          formSchema={actionDialogParams.schema}
        >
          <SetDocumentStatusForm requiredFile={requiredFile} />
        </EditDialogWrapper>
      )}
      {uploadFile && opened && (
        <UploadFileManager
          requiredFile={requiredFile}
          opened={opened}
          setOpened={setOpened}
          setActionDialogParams={resetUseRequiredFileState}
          documentationRequiredId={documentationRequiredId}
          documentationRequiredType={documentationRequiredType}
          loanId={id}
        />
      )}
    </>
  );
};
